import React from "react";
import { Box, Card, CardContent, Typography, Stack, Chip } from "@mui/material";
import { motion } from "framer-motion";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";

const MotionCard = motion(Card);

const glassMixin = {
  backdropFilter: "blur(20px) saturate(180%)",
  WebkitBackdropFilter: "blur(20px) saturate(180%)",
  background: "rgba(255,255,255,0.65)",
  border: "1px solid rgba(255,255,255,0.85)",
  boxShadow:
    "0 8px 32px rgba(16,185,129,0.07), 0 1px 0 rgba(255,255,255,0.9) inset",
};

const getBarColor = (avg) => {
  if (avg >= 80) return "#10b981";
  if (avg >= 65) return "#6366f1";
  if (avg >= 50) return "#f59e0b";
  return "#ef4444";
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const val = payload[0].value;
    const color = getBarColor(val);
    const status =
      val >= 80
        ? "Excellent"
        : val >= 65
        ? "Good"
        : val >= 50
        ? "Fair"
        : "Weak";
    return (
      <Box
        sx={{
          background: "linear-gradient(135deg, #022c22 0%, #064e3b 100%)",
          border: "1px solid rgba(16,185,129,0.3)",
          borderRadius: "14px",
          px: 2,
          py: 1.5,
          minWidth: 130,
          boxShadow: "0 16px 40px rgba(0,0,0,0.3)",
        }}
      >
        <Typography
          sx={{
            color: "#6ee7b7",
            fontSize: "0.68rem",
            fontWeight: 700,
            mb: 0.5,
            letterSpacing: "0.06em",
          }}
        >
          {payload[0].name}
        </Typography>
        <Typography
          sx={{
            color: "#fff",
            fontSize: "1.35rem",
            fontWeight: 800,
            fontFamily: "'Syne', sans-serif",
            lineHeight: 1,
          }}
        >
          {val?.toFixed(1)}%
        </Typography>
        <Typography
          sx={{ color, fontSize: "0.7rem", fontWeight: 700, mt: 0.5 }}
        >
          ● {status}
        </Typography>
      </Box>
    );
  }
  return null;
};

const SubjectChart = ({ data }) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <Card sx={{ mb: 3, borderRadius: "24px", ...glassMixin }}>
        <CardContent
          sx={{
            p: "24px !important",
            textAlign: "center",
            py: "52px !important",
          }}
        >
          <MenuBookRoundedIcon
            sx={{ fontSize: 48, color: "#d1d5db", mb: 1.5 }}
          />
          <Typography
            variant="h6"
            sx={{
              color: "#9ca3af",
              fontFamily: "'Syne', sans-serif",
              fontWeight: 700,
            }}
          >
            No Subject Data
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mt: 0.5 }}>
            No subject data available.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const formatted = data
    .map((item) => ({
      name: item.subject__name || "Unknown",
      avg: parseFloat((item.avg || 0).toFixed(1)),
    }))
    .sort((a, b) => b.avg - a.avg);

  const strongest = formatted[0];
  const weakest = formatted[formatted.length - 1];

  return (
    <MotionCard
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
      sx={{
        mb: 3,
        borderRadius: "24px",
        overflow: "hidden",
        position: "relative",
        ...glassMixin,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: "linear-gradient(90deg, #10b981 0%, #34d399 100%)",
        }}
      />

      <CardContent sx={{ p: "24px !important" }}>
        {/* Header */}
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          mb={3}
          flexWrap="wrap"
          gap={1}
        >
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "12px",
                  background:
                    "linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(52,211,153,0.1) 100%)",
                  border: "1px solid rgba(16,185,129,0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <MenuBookRoundedIcon sx={{ color: "#10b981", fontSize: 20 }} />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Syne', sans-serif",
                  fontWeight: 700,
                  color: "#111827",
                  fontSize: "1rem",
                }}
              >
                Subject Performance
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              sx={{ color: "#9ca3af", ml: "54px", fontSize: "0.78rem" }}
            >
              Average scores by subject
            </Typography>
          </Box>

          {/* Chips */}
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            {strongest && (
              <Chip
                icon={<StarRoundedIcon style={{ fontSize: 13 }} />}
                label={strongest.name}
                size="small"
                sx={{
                  background: "rgba(16,185,129,0.1)",
                  color: "#10b981",
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  border: "1px solid rgba(16,185,129,0.25)",
                  borderRadius: "10px",
                  "& .MuiChip-icon": { color: "inherit" },
                }}
              />
            )}
            {weakest && weakest.name !== strongest?.name && (
              <Chip
                icon={<WarningRoundedIcon style={{ fontSize: 13 }} />}
                label={weakest.name}
                size="small"
                sx={{
                  background: "rgba(239,68,68,0.08)",
                  color: "#ef4444",
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  border: "1px solid rgba(239,68,68,0.2)",
                  borderRadius: "10px",
                  "& .MuiChip-icon": { color: "inherit" },
                }}
              />
            )}
          </Stack>
        </Stack>

        {/* Pie Chart */}
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie
              data={formatted}
              dataKey="avg"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={110}
              label={({ name, percent }) =>
                `${name} ${(percent * 100).toFixed(0)}%`
              }
              labelLine={false}
            >
              {formatted.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={getBarColor(entry.avg)}
                  fillOpacity={0.9}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>

        {/* Legend */}
        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          mt={2.5}
          flexWrap="wrap"
          gap={1}
        >
          {[
            { label: "≥80% Excellent", color: "#10b981" },
            { label: "≥65% Good", color: "#6366f1" },
            { label: "≥50% Fair", color: "#f59e0b" },
            { label: "<50% Weak", color: "#ef4444" },
          ].map((item) => (
            <Stack
              key={item.label}
              direction="row"
              alignItems="center"
              spacing={0.6}
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: item.color,
                }}
              />
              <Typography
                sx={{ fontSize: "0.68rem", color: "#9ca3af", fontWeight: 600 }}
              >
                {item.label}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </CardContent>
    </MotionCard>
  );
};

export default SubjectChart;
